import { useTranslation } from 'react-i18next';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import type { ChargePointData } from '../../types/charge-point.types';
import { getCurrencyDisplay } from '../../utils/price-utils';

interface PowerGroupedSummaryProps {
  chargePoints: ChargePointData[];
}

/**
 * Display comprehensive charge points summary
 * Shows availability, all connectors, power range, types (AC/DC), and price info
 */
export function PowerGroupedSummary({
  chargePoints,
}: PowerGroupedSummaryProps) {
  const { t } = useTranslation();
  const uiMode = useUiModeStore((s) => s.uiMode);

  if (chargePoints.length === 0) {
    return null;
  }

  // Calculate totals
  const totalAvailable = chargePoints.filter(
    (cp) => cp.chargePointState === 'available'
  ).length;
  const totalCount = chargePoints.length;
  const isAvailable = totalAvailable > 0;

  // Get power range
  const powers = chargePoints.map((cp) => cp.maxKw);
  const minPower = Math.min(...powers);
  const maxPower = Math.max(...powers);
  const powerRange =
    minPower === maxPower ? `${minPower} kW` : `${minPower}-${maxPower} kW`;

  // Get unique types (AC/DC)
  const types = [...new Set(chargePoints.map((cp) => cp.typeLabel))];
  const typeLabel = types.join('/');

  // Get all unique connectors from all charge points
  const connectorMap = new Map<
    string,
    { name: string; iconUrl?: string; count: number }
  >();
  chargePoints.forEach((cp) => {
    cp.connectors?.forEach((connector) => {
      const existing = connectorMap.get(connector.name);
      if (existing) {
        existing.count++;
      } else {
        connectorMap.set(connector.name, {
          name: connector.name,
          iconUrl: connector.iconUrl,
          count: 1,
        });
      }
    });
  });
  const uniqueConnectors = Array.from(connectorMap.values());

  // Get price information
  const currency = chargePoints[0]?.currency?.identifier || 'DKK';

  // Check if any charge points have dynamic/spot pricing
  const hasDynamicPricing = chargePoints.some((cp) => {
    const pricing =
      cp.pricingCollection?.pricingsUser ??
      cp.pricingCollection?.pricingsPublic;
    return pricing?.masterPricing?.type === 'spot' || pricing?.dynamic === true;
  });

  let priceDisplay = null;

  if (hasDynamicPricing) {
    // For dynamic pricing, extract numeric values from publicLabel
    const priceValues: number[] = [];
    chargePoints.forEach((cp) => {
      const publicLabel = cp.pricingCollection?.publicLabel;
      if (publicLabel) {
        // Extract numeric value from label like "2.64 DKK/kWh"
        const match = publicLabel.match(/(\d+\.?\d*)/);
        if (match && match[1]) {
          const value = parseFloat(match[1]);
          if (!isNaN(value) && value > 0) {
            priceValues.push(value);
          }
        }
      }
    });

    if (priceValues.length > 0) {
      const minPrice = Math.min(...priceValues);
      const maxPrice = Math.max(...priceValues);
      const priceText =
        minPrice === maxPrice
          ? minPrice.toFixed(2)
          : `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
      priceDisplay = `${priceText} ${getCurrencyDisplay({ identifier: currency })}/kWh`;
    } else {
      // Fallback to label if no price values found
      priceDisplay = t('chargePoint.dynamicPriceForecast');
    }
  } else {
    // For fixed pricing, show price range
    const prices = chargePoints
      .map((cp) => {
        const pricing =
          cp.pricingCollection?.pricingsUser ??
          cp.pricingCollection?.pricingsPublic;
        return pricing?.masterPricing?.amount;
      })
      .filter((p): p is number => p !== undefined && p > 0);

    if (prices.length > 0) {
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const priceText =
        minPrice === maxPrice
          ? minPrice.toFixed(2)
          : `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
      priceDisplay = `${priceText} ${getCurrencyDisplay({ identifier: currency })}/kWh`;
    }
  }

  return (
    <div className="space-y-3 p-4 bg-ink/[0.03] rounded-lg border border-ink/10">
      {/* Availability Badge */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink/80">
          {t('common.status.available')}
        </span>
        <span
          className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold text-white ${
            isAvailable ? 'bg-green-500' : 'bg-gray-400'
          }`}
        >
          {totalAvailable}/{totalCount}
        </span>
      </div>

      {/* Power & Type */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink/80">
          {t('chargePoint.chargeSpeed')}
        </span>
        <div className="text-sm text-ink">
          <span className="font-semibold">{powerRange}</span>
          <span className="text-muted ml-2">{typeLabel}</span>
        </div>
      </div>

      {/* Connectors */}
      {uniqueConnectors.length > 0 && (
        <div
          className={
            uniqueConnectors.length === 1
              ? 'flex items-center justify-between'
              : ''
          }
        >
          <div className="text-sm font-medium text-ink/80">
            {t('chargePoint.connectors')}
          </div>
          <div
            className={`flex items-center gap-2 flex-wrap ${uniqueConnectors.length === 1 ? '' : 'mt-2'}`}
          >
            {uniqueConnectors.map((connector) => (
              <span
                key={connector.name}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-surface text-ink/80 rounded border border-ink/10"
              >
                {connector.iconUrl && (
                  <img
                    src={connector.iconUrl}
                    alt={connector.name}
                    className="w-4 h-4 object-contain"
                  />
                )}
                <span className="font-medium">
                  {connector.name}
                  {connector.count > 1 && (
                    <span className="text-muted ml-1">×{connector.count}</span>
                  )}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Price */}
      {priceDisplay && (
        <div className="flex items-center justify-between pt-2 border-t border-ink/10">
          <span className="text-sm font-medium text-ink/80">
            {uiMode === 'operator'
              ? t('chargePoint.operatorMode.pricingTitle')
              : t('chargePoint.pricing')}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-ink">
              {priceDisplay}
            </span>
            {hasDynamicPricing && (
              <svg
                className="w-4 h-4 text-monta-blue flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-label={t('chargePoint.dynamicPricing')}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
